// src/components/devices/BulkDeviceManager.js
import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { 
  FaPlay, FaSave, FaLightbulb, FaTv, FaCamera, FaVolumeUp, FaThermometerHalf, FaCogs,
  FaSync, FaToggleOn, FaWindowRestore, FaEye, FaEyeSlash, FaArrowUp, FaArrowDown, FaStop
} from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import { Button } from '../../design-system/components';
import './BulkDeviceManager.css';
import { useAuth } from '../../context/AuthContext';

const BulkDeviceManager = () => {
  const { devices, updateDeviceMetadata, controlDevice, refreshDevices } = useContext(AppContext);
  const { apiRequest } = useAuth();

  const [metadata, setMetadata] = useState({ locations: [], deviceTypes: [], floors: [] });
  const [bulkDevices, setBulkDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [predefinedData, setPredefinedData] = useState({
    deviceNames: ['Spot Light', 'Cove Light', 'Chandelier', 'Kitch Cove', 'Curtain', 'Thermostat', 'Presense Sensor','Bathroom Spot Light', 'Bathroom Cove Light'],
    locations: ['Maid Room', 'Kitchen', 'Living Room', 'Bedroom 1', 'Bedroom 2', 'Master Bedroom'],
    floors: ['Ground Floor', 'First Floor', 'Second Floor', 'Top Floor', 'NA'],
    deviceTypes: ['light', 'sensor', 'thermostat', 'curtain', 'switch']
  });

  const API_BASE_URL = '';
  const API_PATH = '/api';

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/metadata`);
        if (response.ok) {
          setMetadata(data);
          setPredefinedData(prev => ({
            ...prev,
            locations: [...new Set([...prev.locations, ...(data.locations || [])])],
            floors: [...new Set([...prev.floors, ...(data.floors || [])])],
            deviceTypes: [...new Set([...prev.deviceTypes, ...(data.deviceTypes || [])])]
          }));
        }
      } catch (error) {
        console.error('Error fetching metadata:', error);
      }
    };
    fetchMetadata();
  }, []);

  useEffect(() => {
    if (devices.length > 0) {
      setBulkDevices(prev => {
        return devices.map(device => {
          const existing = prev.find(d => d.id === device.id);
          if (existing) {
            // Update existing device with fresh data but preserve user changes
            return {
              ...existing,
              controls: device.controls || existing.controls || [],
              sensors: device.sensors || existing.sensors || [],
              linkquality: device.linkquality,
              lastUpdated: device.lastUpdated
            };
          } else {
            // Create new device entry
            return {
              id: device.id,
              parentId: device.parentId,
              originalName: device.name,
              name: device.name ?? '',
              location: device.location ?? '',
              floor: device.floor ?? '',
              type: device.type ?? '',
              deviceType: device.type ?? '',
              hasChanges: false,
              isAssigned: !!(device.name && device.location && device.type),
              controls: device.controls || [],
              sensors: device.sensors || [],
              subtype: device.subtype || ''
            };
          }
        });
      });
    }
  }, [devices]);

  const saveDeviceChanges = async (device) => {
    try {
      const metadata = {
        name: device.name,
        location: device.location,
        type: device.type,
        floor: device.floor
      };
      await updateDeviceMetadata(device.id, metadata);
      setBulkDevices(prev =>
        prev.map(d => d.id === device.id ? { ...d, hasChanges: false, originalName: device.name } : d)
      );
      toast.success(`Saved: ${device.name}`);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(`Failed to save ${device.name}`);
    }
  };

  const handleFieldChange = (deviceId, field, value) => {
    setBulkDevices(prev => {
      const updatedDevices = prev.map(device => {
        if (device.id === deviceId) {
          const updated = { ...device, [field]: value, hasChanges: true };
          updated.isAssigned = !!(updated.name && updated.location && updated.type);
          return updated;
        }
        return device;
      });

      const changedDevice = updatedDevices.find(d => d.id === deviceId);
      if (changedDevice) saveDeviceChanges(changedDevice);

      return updatedDevices;
    });
  };

  const handleRefreshDevices = async () => {
    try {
      setIsLoading(true);
      await refreshDevices();
      toast.success('Device status updated');
    } catch (error) {
      console.error('Refresh error:', error);
      toast.error('Failed to refresh devices');
    } finally {
      setIsLoading(false);
    }
  };

  const getDeviceIcon = (device) => {
    // Check for curtain/cover controls based on exposed properties
    const hasCurtainControl = device.controls?.find(c => 
      c.property === 'state' && 
      c.type === 'enum' && 
      Array.isArray(c.values) &&
      c.values.includes('OPEN') && 
      c.values.includes('CLOSE')
    );
    
    if (hasCurtainControl) {
      return <FaWindowRestore className="text-blue-600" />;
    }

    const type = device.type?.toLowerCase();
    switch (type) {
      case 'light': 
      case 'switch': 
        return <FaLightbulb className="text-yellow-500" />;
        case 'sensor': 
        // Check if it's a presence sensor
          const presenceSensor = device.sensors?.find(s => s.property === 'presence');
          if (presenceSensor) {
            const isPresent = presenceSensor.value === true;
            return isPresent ? <FaEye className="text-green-600" /> : <FaEyeSlash className="text-gray-400" />;
          }
          return <FaCogs className="text-gray-600" />;
      case 'thermostat': 
        return <FaThermometerHalf className="text-red-500" />;
      case 'curtain':
        return <FaWindowRestore className="text-blue-600" />;
      default: 
        return <FaCogs className="text-gray-600" />;
    }
  };

  const unassignedDevices = bulkDevices.filter(d => !d.isAssigned);
  const assignedDevices = bulkDevices.filter(d => d.isAssigned);

  return (
    <div className="bulk-device-manager">
      <div className="bulk-header">
        <h2>Bulk Device Management</h2>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={handleRefreshDevices}
          disabled={isLoading}
          leftIcon={<FaSync />}
          loading={isLoading}
        >
          Refresh Status
        </Button>
      </div>

      {[{ label: 'Unassigned', list: unassignedDevices }, { label: 'Assigned', list: assignedDevices }]
        .filter(section => section.list.length > 0)
        .map(({ label, list }) => (
          <div key={label} className="device-section">
            <h3>{label} Devices ({list.length})</h3>
            <div className="device-table">
              <div className="table-header">
                <div className="col-icon">Status</div>
                <div className="col-id">Device ID</div>
                <div className="col-name">Name</div>
                <div className="col-location">Location</div>
                <div className="col-floor">Floor</div>
                <div className="col-type">Device Type</div>
                <div className="col-actions">Actions</div>
              </div>
              {list.map(device => (
                <DeviceRow
                  key={device.id}
                  device={device}
                  predefinedData={predefinedData}
                  onFieldChange={handleFieldChange}
                  onControl={controlDevice}
                  getDeviceIcon={getDeviceIcon}
                  isLoading={isLoading}
                />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

const DeviceRow = ({ device, predefinedData, onFieldChange, onControl, getDeviceIcon, isLoading }) => {
  const [loading, setLoading] = useState(false);

  const handleControlChange = async (property, value) => {
    try {
      setLoading(true);
      await onControl(device.id, property, value, device.parentId);
    } catch (error) {
      console.error(`Error controlling ${device.name}:`, error);
      toast.error(`Failed to control ${device.name || device.id}`);
    } finally {
      setLoading(false);
    }
  };

  const renderPrimaryControl = () => {
    if (!device.controls || device.controls.length === 0) return null;

    // Check for curtain controls
    const curtainControl = device.controls?.find(c => 
      c.property === 'state' && 
      c.type === 'enum' && 
      Array.isArray(c.options?.values) &&
      c.options.values.includes('OPEN') && 
      c.options.values.includes('CLOSE')
    );
    
    if (curtainControl) {
      const currentState = curtainControl.value;
      return (
        <div className="flex gap-1">
          <Button
            variant={currentState === 'OPEN' ? 'success' : 'secondary'}
            size="sm"
            onClick={() => handleControlChange(curtainControl.property, 'OPEN')}
            disabled={loading || currentState === 'OPEN'}
            className="px-2"
            leftIcon={<FaArrowUp />}
          >
            Open
          </Button>
          <Button
            variant={currentState === 'CLOSE' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => handleControlChange(curtainControl.property, 'CLOSE')}
            disabled={loading || currentState === 'CLOSE'}
            className="px-2"
            leftIcon={<FaArrowDown />}
          >
            Close
          </Button>
        </div>
      );
    }

    // Check for binary controls (switches, lights)
    const binaryControl = device.controls.find(c => c.valueType === 'binary');
    if (binaryControl) {
      const isOn = binaryControl.value === true || binaryControl.value === 'ON' || binaryControl.value === binaryControl.options?.on;
      const toggleValue = isOn ? binaryControl.options?.off || false : binaryControl.options?.on || true;
      
      return (
        <Button
          variant={isOn ? 'success' : 'secondary'}
          size="sm"
          onClick={() => handleControlChange(binaryControl.property, toggleValue)}
          disabled={loading}
          leftIcon={<FaToggleOn />}
        >
          {isOn ? 'ON' : 'OFF'}
        </Button>
      );
    }

    return null;
  };

  return (
    <div className="device-row">
      <div className="col-icon">{getDeviceIcon(device)}</div>
      <div className="col-id">{device.id.length > 15 ? `${device.id.slice(0, 15)}...` : device.id}</div>
      <div className="col-name">
        <select value={device.name} onChange={e => onFieldChange(device.id, 'name', e.target.value)} className="field-select">
          <option value="">Select Name</option>
          {predefinedData.deviceNames.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
      </div>
      <div className="col-location">
        <select value={device.location} onChange={e => onFieldChange(device.id, 'location', e.target.value)} className="field-select">
          <option value="">Select Location</option>
          {predefinedData.locations.map(location => <option key={location} value={location}>{location}</option>)}
        </select>
      </div>
      <div className="col-floor">
        <select value={device.floor} onChange={e => onFieldChange(device.id, 'floor', e.target.value)} className="field-select">
          <option value="">Select Floor</option>
          {predefinedData.floors.map(floor => <option key={floor} value={floor}>{floor}</option>)}
        </select>
      </div>
      <div className="col-type">
        <select value={device.type} onChange={e => onFieldChange(device.id, 'type', e.target.value)} className="field-select">
          <option value="">Select Type</option>
          {predefinedData.deviceTypes.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
      </div>
      <div className="col-actions">
        {renderPrimaryControl()}
      </div>
    </div>
  );
};

export default BulkDeviceManager;
